import React, { useContext, useEffect, useMemo, useState } from "react"
import { NavLink } from "react-router-dom"
import moment from "moment"
import firebase from "firebase"

import { AuthContext } from "../../utils/providers/auth"
import { db } from "../../utils/firebase"
import { CALENDAR_MONTHS } from "../../utils/exports/months"

import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Select from "../../components/ui/select/select"
import Button from "../../components/ui/button/button"
import Table from "../../components/structure/table/table"
import Badge from "../../components/ui/badge/badge"
import Checkbox from "../../components/ui/checkbox/checkbox"

import { CONFIRMATION_STATUS, ITEM_KIND } from "../booking/tabs/_components/confirmations/helpers"

import "./confirmations-report.scss"

/**
 * Process `items` through `worker(item)` with at most `concurrency` promises
 * in flight at any time. Used to throttle the per-booking subcollection
 * reads — without this, an unfiltered query (~900 bookings × ~10 reads each)
 * blows past Firestore's client-side limits and throws "Too many outstanding
 * requests".
 */
async function mapWithConcurrency(items, concurrency, worker) {
	const results = new Array(items.length)
	let cursor = 0

	async function run() {
		while (cursor < items.length) {
			const idx = cursor++
			results[idx] = await worker(items[idx], idx)
		}
	}

	const lanes = Array.from({ length: Math.min(concurrency, items.length) }, run)
	await Promise.all(lanes)
	return results
}

/**
 * Single-item status for the flight / transfer columns. These items only ever
 * exist 0-or-1 times on a booking, so rather than a count they resolve to one
 * badge state:
 *   ABSENT    — the booking has no flight / transfer at all.
 *   AWAITING  — item exists but has no confirmation entry yet.
 *   ACTION    — supplier reported it NOT confirmed (needs chasing).
 *   CONFIRMED — supplier has confirmed it.
 */
const ITEM_STATUS = {
	ABSENT: "ABSENT",
	AWAITING: "AWAITING",
	ACTION: "ACTION",
	CONFIRMED: "CONFIRMED",
}

function singleItemStatus(present, entry) {
	if (!present) return ITEM_STATUS.ABSENT
	if (entry?.status === CONFIRMATION_STATUS.CONFIRMED) return ITEM_STATUS.CONFIRMED
	if (entry?.status === CONFIRMATION_STATUS.NOT_CONFIRMED) return ITEM_STATUS.ACTION
	return ITEM_STATUS.AWAITING
}

/**
 * Per-booking confirmation tally. Fetches every supplier-touching item on the
 * booking (hotels, rounds, transfer, flight) and, for each one, looks at the
 * latest entry in its confirmations subcollection to decide whether it counts
 * as confirmed.
 *
 * Hotels + golf rounds drive the main Status column and the `confirmed/required`
 * count. Flight and transfer are reported separately as their own single-status
 * badge columns, so they're excluded from that count.
 *
 * Mirrors the logic in the booking Confirmations tab so the counts here line
 * up with what an agent sees when they open the tab itself.
 */
async function tallyConfirmations(bookingID, bookingDoc) {
	const items = []

	const hotelsOrder = Array.isArray(bookingDoc.hotels_order) ? bookingDoc.hotels_order : []
	hotelsOrder.forEach((hid) => items.push({ kind: ITEM_KIND.HOTEL, itemID: hid }))

	const roundsOrder = Array.isArray(bookingDoc.rounds_order) ? bookingDoc.rounds_order : []
	roundsOrder.forEach((rid) => items.push({ kind: ITEM_KIND.ROUND, itemID: rid }))

	const hasTransfer = Boolean(bookingDoc.transfer)
	if (hasTransfer) {
		items.push({ kind: ITEM_KIND.TRANSFER, itemID: null })
	}

	const hasFlight = Boolean(bookingDoc.flight && bookingDoc.flight.itineraryID)
	if (hasFlight) {
		items.push({ kind: ITEM_KIND.FLIGHT, itemID: null })
	}

	const latestEntries = await Promise.all(
		items.map(async (item) => {
			const path =
				item.kind === ITEM_KIND.HOTEL
					? `bookings/${bookingID}/hotels/${item.itemID}/confirmations`
					: item.kind === ITEM_KIND.ROUND
						? `bookings/${bookingID}/rounds/${item.itemID}/confirmations`
						: item.kind === ITEM_KIND.TRANSFER
							? `bookings/${bookingID}/transfer_confirmations`
							: `bookings/${bookingID}/flight_confirmations`

			try {
				const snap = await db.collection(path).orderBy("created", "desc").limit(1).get()
				return { kind: item.kind, entry: snap.docs[0]?.data() || null }
			} catch (e) {
				return { kind: item.kind, entry: null }
			}
		})
	)

	// Hotels + golf drive the main Status column and the confirmed/required count.
	const accommodation = latestEntries.filter(
		(e) => e.kind === ITEM_KIND.HOTEL || e.kind === ITEM_KIND.ROUND
	)
	const required = accommodation.length
	const confirmed = accommodation.filter((e) => e.entry?.status === CONFIRMATION_STATUS.CONFIRMED).length
	const needsAction = accommodation.filter((e) => e.entry?.status === CONFIRMATION_STATUS.NOT_CONFIRMED).length

	// Flight + transfer each render as their own single-status badge column.
	const flightEntry = latestEntries.find((e) => e.kind === ITEM_KIND.FLIGHT)?.entry || null
	const transferEntry = latestEntries.find((e) => e.kind === ITEM_KIND.TRANSFER)?.entry || null
	const flightStatus = singleItemStatus(hasFlight, flightEntry)
	const transferStatus = singleItemStatus(hasTransfer, transferEntry)

	// Last confirmed = most recent received_date across every confirmed item
	// (including flight + transfer). Falls back to `created` if received_date is
	// missing.
	let lastConfirmedSeconds = null
	latestEntries.forEach(({ entry }) => {
		if (!entry || entry.status !== CONFIRMATION_STATUS.CONFIRMED) return
		const ts = entry.received_date?.seconds || entry.created?.seconds || null
		if (ts && (!lastConfirmedSeconds || ts > lastConfirmedSeconds)) {
			lastConfirmedSeconds = ts
		}
	})

	return { required, confirmed, needsAction, flightStatus, transferStatus, lastConfirmedSeconds }
}

/**
 * Badge for a single-item (flight / transfer) status. `absentLabel` is shown
 * when the booking has no such item (muted, like the "No items" main badge).
 */
function ItemStatusBadge({ status, absentLabel }) {
	switch (status) {
		case ITEM_STATUS.CONFIRMED:
			return <Badge type="POSITIVE" label="Confirmed" />
		case ITEM_STATUS.ACTION:
			return <Badge type="AMBER" label="Action needed" />
		case ITEM_STATUS.AWAITING:
			return <Badge type="AMBER" label="Awaiting" />
		default:
			return <Badge type="" label={absentLabel} />
	}
}

const HEADINGS = [
	"Reference",
	"Lead pax",
	"Agent",
	"Travelling",
	"Last confirmed",
	"Confirmations",
	"Status",
	"Flights",
	"Transfers",
	"",
]

/**
 * Confirmations report — lists bookings travelling in the chosen month/year
 * (optionally filtered by agent) with the confirmation totals for each one.
 *
 * Travelling = booking's check_in falls inside the chosen month window. Reads
 * the same per-item confirmation subcollections that the booking-level
 * Confirmations tab writes to.
 */
export default function ConfirmationsReport() {
	const { user } = useContext(AuthContext)

	const [agents, setAgents] = useState({})
	const [years, setYears] = useState({})

	const [agent, setAgent] = useState("")
	const [month, setMonth] = useState("")
	const [year, setYear] = useState("")

	const [loading, setLoading] = useState(false)
	const [rows, setRows] = useState([])
	const [hasFetched, setHasFetched] = useState(false)
	const [hideFullyConfirmed, setHideFullyConfirmed] = useState(false)

	// Load full agent list — this page is visible to everyone so any agent can
	// filter by any other agent (matches the spec: "agent (optional)").
	useEffect(() => {
		db.collection("users")
			.orderBy("first_name")
			.get()
			.then((snap) => {
				const next = {}
				snap.forEach((doc) => {
					const { first_name, last_name } = doc.data()
					next[doc.id] = `${first_name || ""} ${last_name || ""}`.trim()
				})
				setAgents(next)
			})
	}, [])

	// Year picker — 2018 → current + 2 (same window as the Stats page).
	useEffect(() => {
		const next = {}
		const upper = Number(moment().add(2, "years").format("YYYY"))
		for (let y = upper; y >= 2018; y--) {
			next[y] = `${y}`
		}
		setYears(next)
	}, [])

	const canFetch = Boolean(month && year)

	const fetchRows = async () => {
		if (!canFetch) return
		setLoading(true)
		setHasFetched(true)
		setRows([])

		const start = moment(`${month}-${year}`, "MMMM-YYYY").startOf("month").valueOf()
		const end = moment(`${month}-${year}`, "MMMM-YYYY").endOf("month").valueOf()
		const startTs = firebase.firestore.Timestamp.fromMillis(start)
		const endTs = firebase.firestore.Timestamp.fromMillis(end)

		try {
			let query = db
				.collection("bookings")
				.where("check_in", ">=", startTs)
				.where("check_in", "<=", endTs)
				.where("removed", "==", false)
				.orderBy("check_in")

			if (agent?.option) {
				query = query.where("agent", "==", agent.option)
			}

			const snap = await query.get()
			const bookings = snap.docs.map((d) => ({ id: d.id, ...d.data() }))

			// Pull lead client names + agent names in parallel (one read each, cached
			// by Firestore for repeats).
			const clientIDs = [...new Set(bookings.map((b) => b.client).filter(Boolean))]
			const agentIDs = [...new Set(bookings.map((b) => b.agent).filter(Boolean))]

			const [clientDocs, agentDocs] = await Promise.all([
				mapWithConcurrency(clientIDs, 25, (id) =>
					db
						.doc(`clients/${id}`)
						.get()
						.then((doc) => ({ id, data: doc.data() }))
						.catch(() => ({ id, data: null }))
				),
				mapWithConcurrency(agentIDs, 25, (id) =>
					db
						.doc(`users/${id}`)
						.get()
						.then((doc) => ({ id, data: doc.data() }))
						.catch(() => ({ id, data: null }))
				),
			])

			const clientLookup = {}
			clientDocs.forEach(({ id, data }) => {
				if (data) clientLookup[id] = `${data.first_name || ""} ${data.last_name || ""}`.trim()
			})
			const agentLookup = {}
			agentDocs.forEach(({ id, data }) => {
				if (data) agentLookup[id] = `${data.first_name || ""} ${data.last_name || ""}`.trim()
			})

			// Tally confirmations per booking. Each tally fans out to ~hotels +
			// rounds + transfer + flight subcollection reads, so without a
			// concurrency cap an unfiltered month (~900 bookings × ~10 reads)
			// blows past Firestore's client-side outstanding-request limit.
			const tallies = await mapWithConcurrency(bookings, 10, (b) => tallyConfirmations(b.id, b))

			const next = bookings.map((b, i) => {
				const tally = tallies[i]
				return {
					id: b.id,
					reference: b.reference || b.id,
					leadPax: clientLookup[b.client] || "—",
					agentName: agentLookup[b.agent] || "—",
					checkIn: b.check_in?.seconds || null,
					required: tally.required,
					confirmed: tally.confirmed,
					needsAction: tally.needsAction,
					flightStatus: tally.flightStatus,
					transferStatus: tally.transferStatus,
					lastConfirmedSeconds: tally.lastConfirmedSeconds,
				}
			})

			setRows(next)
		} catch (error) {
			console.error("Confirmations report fetch failed:", error)
		}

		setLoading(false)
	}

	const totals = useMemo(() => {
		const required = rows.reduce((sum, r) => sum + r.required, 0)
		const confirmed = rows.reduce((sum, r) => sum + r.confirmed, 0)
		const outstanding = required - confirmed
		return { required, confirmed, outstanding }
	}, [rows])

	// Apply the "hide fully confirmed" filter at render time so the summary
	// totals above keep showing the full picture. Only hotels + golf decide
	// whether a booking is "fully confirmed" here — flights/transfers are
	// ignored, so a row drops out once its hotels and courses are all confirmed
	// even if a flight/transfer is still outstanding.
	const visibleRows = useMemo(() => {
		if (!hideFullyConfirmed) return rows
		return rows.filter((r) => r.required === 0 || r.confirmed < r.required)
	}, [rows, hideFullyConfirmed])

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Confirmations</h1>
			</Title>

			<div className="page-filters confirmations-report-filters">
				<Select
					label="Agent:"
					placeholder="Agent (optional):"
					value={agent}
					selected={agent.option}
					activeOnHover={true}
					onSelect={(option) => setAgent(option)}
					options={agents}
				/>

				<Select
					label="Month:"
					placeholder="Month:"
					value={month}
					activeOnHover={true}
					onSelect={(option) => setMonth(option.option)}
					options={CALENDAR_MONTHS}
				/>

				<Select
					label="Year:"
					placeholder="Year:"
					value={year}
					activeOnHover={true}
					onSelect={(option) => setYear(option.option)}
					options={years}
				/>

				<Button
					filters={true}
					disabled={!canFetch}
					label="Fetch confirmations"
					loadingText="Fetching..."
					loading={loading}
					onClick={fetchRows}
				/>

				{agent?.option && (
					<button
						type="button"
						className="confirmations-report-clear"
						onClick={() => setAgent("")}>
						Clear agent
					</button>
				)}
			</div>

			{hasFetched && !loading && rows.length > 0 && (
				<div className="confirmations-report-summary">
					<div className="confirmations-report-summary-stats">
						<span>
							<strong>{rows.length}</strong> booking{rows.length === 1 ? "" : "s"}
						</span>
						<span>
							<strong>{totals.confirmed}</strong> of <strong>{totals.required}</strong> items confirmed
						</span>
						{totals.outstanding > 0 && (
							<span className="confirmations-report-summary-warning">
								{totals.outstanding} outstanding
							</span>
						)}
					</div>

					<Checkbox
						checked={hideFullyConfirmed}
						label="Hide fully confirmed"
						onClick={() => setHideFullyConfirmed((v) => !v)}
					/>
				</div>
			)}

			<Table
				className="confirmations-report-table"
				headings={HEADINGS}
				noResults={hasFetched && !loading && visibleRows.length === 0}
				noResultsMessage={
					hideFullyConfirmed && rows.length > 0
						? "All bookings in this window are fully confirmed."
						: "No bookings travelling in that window."
				}>
				{visibleRows.map((row) => {
					const outstanding = row.required - row.confirmed
					const hasOutstanding = outstanding > 0
					const allConfirmed = !hasOutstanding && row.required > 0
					const noItems = row.required === 0

					return (
						<tr key={row.id}>
							<td>
								<NavLink to={`/booking/${row.id}`}>{row.reference}</NavLink>
							</td>
							<td>{row.leadPax}</td>
							<td>{row.agentName}</td>
							<td>{row.checkIn ? moment(row.checkIn, "X").format("DD/MM/YYYY") : "—"}</td>
							<td>
								{row.lastConfirmedSeconds
									? moment(row.lastConfirmedSeconds, "X").format("DD/MM/YYYY")
									: <small className="no-value">—</small>}
							</td>
							<td>
								<span className="confirmations-report-count">
									{row.confirmed} / {row.required}
								</span>
								{row.needsAction > 0 && (
									<span className="confirmations-report-needs-action">
										· {row.needsAction} flagged
									</span>
								)}
							</td>
							<td>
								{noItems && <Badge type="" label="No items" />}
								{allConfirmed && <Badge type="POSITIVE" label="All confirmed" />}
								{hasOutstanding && <Badge type="AMBER" label={`Awaiting ${outstanding}`} />}
							</td>
							<td>
								<ItemStatusBadge status={row.flightStatus} absentLabel="No flight" />
							</td>
							<td>
								<ItemStatusBadge status={row.transferStatus} absentLabel="No transfer" />
							</td>
							<td className="is-button">
								<NavLink to={`/booking/${row.id}`}>View booking</NavLink>
								<a
									target="_blank"
									rel="noreferrer"
									href={`/booking/${row.id}`}>
									View in new tab
								</a>
							</td>
						</tr>
					)
				})}
			</Table>
		</Tile>
	)
}
